import type { Ecriture } from '@shared/types/Ecriture';
import { PLAN_COMPTABLE_OHADA } from '@shared/constants/PlanComptable';
import { uuid } from '@shared/utils/uuid';
import { roundFCFA } from '@shared/utils/round';

export interface ProvisionParams {
  encours_clients: number;
  masse_salariale: number;
  annee: string;
}

export interface ProvisionsResult {
  ecritures: Ecriture[];
  total_creances_douteuses: number;
  total_conges_payes: number;
  total_indemnites_fin_carriere: number;
  total_general: number;
}

/**
 * Génération automatique des provisions OHADA à la clôture d'exercice.
 * AUDCIF Art. 6 et 8.
 *
 * - Créances douteuses : 5 % des créances clients (compte 491)
 * - Congés payés : 8,33 % (1/12) de la masse salariale (compte 198)
 * - Indemnités fin de carrière : 2 % de la masse salariale (compte 1910)
 */
export class ProvisionsOHADAService {
  static readonly TAUX_CREANCES_DOUTEUSES = 0.05;
  static readonly TAUX_CONGES_PAYES = 1 / 12;
  static readonly TAUX_INDEMNITES_FIN_CARRIERE = 0.02;

  static genererEcritures(tenant_id: string, params: ProvisionParams): ProvisionsResult {
    const dateEcr = `31/12/${params.annee}`;
    const periode = `${params.annee}-12`;
    const ecritures: Ecriture[] = [];

    const total_creances_douteuses = roundFCFA(
      params.encours_clients * this.TAUX_CREANCES_DOUTEUSES,
    );
    const total_conges_payes = roundFCFA(params.masse_salariale * this.TAUX_CONGES_PAYES);
    const total_indemnites_fin_carriere = roundFCFA(
      params.masse_salariale * this.TAUX_INDEMNITES_FIN_CARRIERE,
    );

    const pousser = (
      compteDebit: string,
      compteCredit: string,
      montant: number,
      libelle: string,
    ): void => {
      if (montant <= 0) return;
      ecritures.push(this.ecriture(tenant_id, dateEcr, periode, compteDebit, montant, 0, libelle));
      ecritures.push(this.ecriture(tenant_id, dateEcr, periode, compteCredit, 0, montant, libelle));
    };

    pousser('6594', '491', total_creances_douteuses, 'Provision créances douteuses (5%) - clôture');
    pousser('6815', '198', total_conges_payes, 'Provision congés payés (8,33%) - clôture');
    pousser(
      '6911',
      '1910',
      total_indemnites_fin_carriere,
      'Provision indemnités fin de carrière (2%) - clôture',
    );

    return {
      ecritures,
      total_creances_douteuses,
      total_conges_payes,
      total_indemnites_fin_carriere,
      total_general: total_creances_douteuses + total_conges_payes + total_indemnites_fin_carriere,
    };
  }

  private static ecriture(
    tenant_id: string,
    date: string,
    periode: string,
    compte: string,
    debit: number,
    credit: number,
    libelle: string,
  ): Ecriture {
    return {
      id: uuid('ecr'),
      tenant_id,
      date,
      periode,
      journal: 'OD',
      compte,
      libelle_compte: PLAN_COMPTABLE_OHADA[compte]?.libelle ?? '',
      libelle,
      debit,
      credit,
      source: 'provision',
      genere_le: new Date().toISOString(),
    };
  }
}
